import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Helmet from '../lib/Helmet';
import useCart from '../hooks/useCart';
import { clearCart, updateLine, openCart } from '../lib/cart';
import { EXCHANGE, DELIVERY, BUSINESS, KIT_COPPER_METRES } from '../data/pricing';
import { lookupPincode, deliveryCharge, isValidPincode } from '../data/pincodes';
import { priceQuote, newQuoteRef, formatINR } from '../lib/quote';
import { deliveryLabel, deliveryDays, shortDate } from '../lib/deliveryDate';
import {
  loadCustomer, saveCustomer, forgetCustomer, savedPincode, savePincode, floorText,
} from '../lib/customer';
import { startPayment } from '../lib/razorpay';
import { saveLead } from '../lib/api';
import { IMG_FALLBACK } from '../lib/images';
import { track, FUNNEL, trackQuoteGenerated, trackPurchase, setAdsUserData } from '../lib/track';

// Route: /checkout  (/get-quote still lands here)
//
// One page, and the button at the bottom pays. No "quote confirmed" stop, no
// second confirmation: the patterns every serious Indian D2C checkout uses.
//
//   Mobile first, +91 fixed. No OTP: an order needs a real payment anyway.
//   Pincode already known from the product page or cart, so city, state,
//     delivery date and charge are confirmed before anything is typed.
//   An address built for an AC delivery: floor and lift matter when two men
//     carry a 40 kg outdoor unit.
//   Every field carries the autocomplete name Chrome expects, so its autofill
//     can fill the whole form in one tap.
//   GSTIN for business buyers, so they can claim input tax credit.
//   Installation asked once, for the whole order, off by default.
//   Returning customers see their saved details and pay in one tap.
//
// The payment window opens straight from the Pay button. A closed or failed
// payment keeps everything on screen; one tap tries again.

const LAST_ORDER_KEY = 'cc_last_order';
// Set by /resume when a customer opens the team's offer email. The server
// checks the code again before charging, so this only decides what is shown.
const OFFER_KEY = 'cc_offer';

// Book now, pay before dispatch: today's payment is the delivery charge, or
// this amount when delivery is free. The unit is held for three days.
const BOOKING_MIN = 999;
const BOOKING_HOLD_DAYS = 3;

function readOffer() {
  try {
    const o = JSON.parse(localStorage.getItem(OFFER_KEY) || 'null');
    if (o && o.code && Number(o.amount) > 0 && new Date(o.expiresAt) > new Date()) return o;
  } catch { /* storage off */ }
  return null;
}
const MAX_UNITS = 6;
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

// Set to true once Razorpay starts adding the convenience fee, so nobody is
// surprised by it in the payment window.
const FEE_ACTIVE = false;

function initialForm(saved) {
  const pdpPin = savedPincode();
  const a = saved?.address || {};
  // A pincode checked on a product page since the last order means a new
  // delivery address: keep the person, not the old address.
  const sameAddress = !pdpPin || !a.pincode || pdpPin === a.pincode;
  return {
    // A first-time buyer who took the welcome code: their number is known.
    mobile: saved?.mobile || readOffer()?.mobile || '',
    fullName: saved?.fullName || '',
    email: saved?.email || '',
    pincode: sameAddress ? (a.pincode || pdpPin || '') : pdpPin,
    house: sameAddress ? (a.house || '') : '',
    floor: sameAddress ? (a.floor ?? '') : '',
    lift: sameAddress ? (a.lift || '') : '',
    area: sameAddress ? (a.area || '') : '',
    landmark: sameAddress ? (a.landmark || '') : '',
    label: a.label || 'Home',
    gstOn: Boolean(saved?.gstin),
    gstin: saved?.gstin || '',
    company: saved?.company || '',
    prefDay: '',
  };
}

const bandLabel = (ton) =>
  (EXCHANGE.copper.find((b) => String(b.maxTon) === String(ton)) || {}).label || `${ton} ton`;

function exchangeSummary(quote) {
  return quote.lines
    .filter((l) => l.exchangeAmount > 0)
    .map((l) => `${l.qty}x ${l.exchangeCoil === 'aluminium' ? 'aluminium coil' : `${bandLabel(l.exchangeTon)} copper`} (-${formatINR(l.exchangeAmount)})`)
    .join('; ');
}

function addressLines(form, pin) {
  const access = floorText(form.floor, form.lift);
  return [
    [form.house.trim(), access].filter(Boolean).join(', '),
    [form.area.trim(), form.landmark.trim() && `near ${form.landmark.trim()}`].filter(Boolean).join(', '),
    [pin?.city, pin?.state, form.pincode].filter(Boolean).join(', '),
  ].filter(Boolean);
}

// The lead row in Supabase has fixed columns. Floor and lift travel inside
// house_no (so they reach every address line, including the confirmation
// email), and the rest inside notes.
function toLeadForm(form, install, quote) {
  const ex = exchangeSummary(quote);
  return {
    fullName: form.fullName.trim(),
    mobile: form.mobile,
    email: form.email.trim(),
    pincode: form.pincode,
    houseNo: [form.house.trim(), floorText(form.floor, form.lift)].filter(Boolean).join(', '),
    street: form.area.trim(),
    landmark: form.landmark.trim(),
    notes: [
      `Installation: ${install ? 'YES, survey and quote needed' : 'no'}`,
      `Preferred delivery: ${form.prefDay || 'no preference'}`,
      `Address type: ${form.label}`,
      form.gstOn && form.gstin ? `GSTIN: ${form.gstin.trim().toUpperCase()} (${form.company.trim()})` : '',
      ex ? `Exchange: ${ex}` : '',
    ].filter(Boolean).join(' · '),
  };
}

const inputCls = (bad) =>
  `mt-1.5 block h-[50px] w-full rounded-xl border bg-white px-3.5 text-base text-gray-900 outline-none transition ${
    bad ? 'border-red-400 bg-red-50/40' : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
  }`;

function Field({ label, optional, hint, error, children, className = '' }) {
  return (
    <div className={className} data-bad={error ? '1' : '0'}>
      <label className="block">
        <span className="text-[13px] font-semibold text-gray-700">
          {label}
          {optional && <span className="ml-1 font-normal text-gray-500">(optional)</span>}
        </span>
        {children}
      </label>
      {error
        ? <p className="mt-1 text-[12px] text-red-600">{error}</p>
        : hint ? <p className="mt-1 text-[12px] text-gray-600">{hint}</p> : null}
    </div>
  );
}

function Chip({ on, onClick, children }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on}
      className={on
        ? 'min-h-[44px] rounded-xl border-2 border-blue-600 bg-blue-50 px-3.5 text-sm font-semibold text-blue-800'
        : 'min-h-[44px] rounded-xl border border-gray-300 bg-white px-3.5 text-sm text-gray-700 hover:border-gray-400'}>
      {children}
    </button>
  );
}

function PlanOption({ on, onClick, title, amount, children }) {
  return (
    <button type="button" role="radio" aria-checked={on} onClick={onClick}
      className={`flex w-full gap-3 rounded-xl p-3.5 text-left transition ${on ? 'border-2 border-blue-600 bg-blue-50' : 'border border-gray-300 bg-white hover:border-gray-400'}`}>
      <span className={`mt-0.5 block h-5 w-5 shrink-0 rounded-full border-2 ${on ? 'border-[6px] border-blue-600 bg-white' : 'border-gray-400 bg-white'}`} aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="text-[15px] font-bold text-gray-900">{title}</span>
          <span className="shrink-0 text-[15px] font-bold tabular-nums text-gray-900">{amount}</span>
        </span>
        <span className="mt-0.5 block text-[13px] leading-snug text-gray-600">{children}</span>
      </span>
    </button>
  );
}

function Card({ title, aside, children }) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4">
      {title && (
        <div className="mb-3 flex items-baseline justify-between gap-2">
          <h2 className="text-base font-bold text-gray-900">{title}</h2>
          {aside && <span className="text-[13px] text-gray-600">{aside}</span>}
        </div>
      )}
      {children}
    </section>
  );
}

const Check = ({ className = 'h-4 w-4' }) => (
  <svg viewBox="0 0 24 24" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2.4"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
);
const Lock = ({ className = 'h-4 w-4' }) => (
  <svg viewBox="0 0 24 24" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

function PriceLines({ quote, delivery, offer, total }) {
  return (
    <dl className="text-sm">
      <div className="flex justify-between py-1 text-gray-800">
        <dt>Items ({quote.units})</dt><dd className="tabular-nums">{formatINR(quote.equipment)}</dd>
      </div>
      <div className="flex justify-between py-1 text-gray-800">
        <dt>Delivery</dt>
        <dd className="tabular-nums">
          {!delivery ? <span className="text-gray-500">Add pincode</span>
            : delivery.free ? <span className="font-semibold text-green-700">Free</span>
              : formatINR(delivery.price)}
        </dd>
      </div>
      {quote.exchangeTotal > 0 && (
        <div className="flex justify-between py-1 font-medium text-green-700">
          <dt>Old AC exchange credit</dt><dd className="tabular-nums">&minus; {formatINR(quote.exchangeTotal)}</dd>
        </div>
      )}
      {offer && (
        <div className="flex justify-between py-1 font-medium text-green-700">
          <dt>Offer {offer.code}</dt><dd className="tabular-nums">&minus; {formatINR(offer.amount)}</dd>
        </div>
      )}
      <div className="mt-1.5 flex justify-between border-t border-dashed border-gray-300 pt-2.5 text-[17px] font-bold text-gray-900">
        <dt>Order total</dt><dd className="tabular-nums">{formatINR(total ?? quote.total)}</dd>
      </div>
      {quote.savings > 0 && (
        <p className="mt-2 rounded-lg bg-green-50 px-3 py-2 text-[13px] font-semibold text-green-700">
          You save {formatINR(quote.savings)} on MRP
        </p>
      )}
    </dl>
  );
}

function ItemList({ quote }) {
  return (
    <ul className="divide-y divide-gray-100">
      {quote.lines.map((l) => (
        <li key={l.lineId} className="flex gap-3 py-2.5">
          <img src={l.model.image} alt="" width="56" height="56"
            onError={(e) => { e.currentTarget.src = IMG_FALLBACK; }}
            className="h-14 w-14 shrink-0 rounded-lg bg-gray-50 object-contain p-1" />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold leading-snug text-gray-900">{l.model.title}</p>
            <p className="text-[12px] text-gray-600">
              {l.kit === 'withKit' ? `With kit, ${KIT_COPPER_METRES} m copper` : 'Without kit'} · Qty {l.qty}
            </p>
          </div>
          <p className="shrink-0 text-[13px] font-semibold tabular-nums text-gray-900">{formatINR(l.equipment)}</p>
        </li>
      ))}
    </ul>
  );
}

export default function Checkout() {
  const lines = useCart();
  const navigate = useNavigate();
  const { state: navState } = useLocation();

  const saved = useMemo(() => loadCustomer(), []);
  const [form, setForm] = useState(() => initialForm(saved));
  const [useSaved, setUseSaved] = useState(() => {
    const f = initialForm(saved);
    return Boolean(saved && f.house && f.area && f.pincode);
  });
  const [install, setInstall] = useState(() => lines.some((l) => l.withInstall === true));
  const [touched, setTouched] = useState(false);
  const [showItems, setShowItems] = useState(false);
  const [status, setStatus] = useState({ kind: 'idle', message: '' });
  const [ref, setRef] = useState('');
  const [captured, setCaptured] = useState('');
  const [plan, setPlan] = useState('full');
  const [offerRaw, setOfferRaw] = useState(() => readOffer());
  const leadSaved = useRef(new Set());

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const digits = (k, max) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value.replace(/\D/g, '').slice(0, max) }));
  const pick = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pin = useMemo(
    () => (isValidPincode(form.pincode) ? lookupPincode(form.pincode) : null),
    [form.pincode],
  );
  const base = useMemo(() => priceQuote(lines), [lines]);
  const delivery = useMemo(
    () => (pin?.ok ? deliveryCharge(pin, { orderValue: base.equipment, units: base.units }) : null),
    [pin, base.equipment, base.units],
  );
  const quote = useMemo(() => priceQuote(lines, { delivery }), [lines, delivery]);
  const days = useMemo(() => deliveryDays(3), []);

  // The offer belongs to the mobile number it was sent to. The Diwali
  // welcome offer is for new-stock ACs only; the server checks both again.
  const welcomeBlocked = Boolean(offerRaw?.welcome) && quote.lines.length > 0 && !quote.lines.some((l) => !l.model.clearance);
  const offer = offerRaw && !welcomeBlocked && (!offerRaw.mobile || !form.mobile || offerRaw.mobile === form.mobile) ? offerRaw : null;
  const orderTotal = Math.max(0, quote.total - (offer ? Number(offer.amount) : 0));
  const deliveryPrice = delivery && !delivery.free ? Number(delivery.price) || 0 : 0;
  const bookNow = Math.min(orderTotal, deliveryPrice > 0 ? deliveryPrice : BOOKING_MIN);
  const balance = Math.max(0, orderTotal - bookNow);

  // Abandoned checkout: a valid mobile and a serviceable pincode are enough
  // to follow up, so save them even if the customer never taps Pay. Debounced,
  // and sent once per phone-and-pincode pair.
  useEffect(() => {
    const phoneOk = /^[6-9]\d{9}$/.test(form.mobile);
    if (!phoneOk || !pin?.ok || ref) return undefined;
    const key = `${form.mobile}:${form.pincode}`;
    if (captured === key) return undefined;
    const t = window.setTimeout(() => {
      setCaptured(key);
      setAdsUserData(form, pin);
      saveLead({ ref: `AB-${form.mobile}-${new Date().toISOString().slice(2, 10).replace(/-/g, "")}`, kind: 'abandoned', quote, form: toLeadForm(form, install, quote), pin });
    }, 2500);
    return () => window.clearTimeout(t);
  }, [form, pin, quote, captured, ref, install]);

  if (!lines.length) {
    return (
      <main className="mx-auto max-w-xl px-4 pb-16 pt-28 text-center">
        <Helmet>
          <title>Checkout | Citi Cool</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h1 className="text-xl font-bold text-gray-900">Your cart is empty</h1>
        <p className="mt-2 text-sm text-gray-600">Pick an AC and you will be back here in a tap.</p>
        <Link to="/daikin-ac-price-list-delhi"
          className="mt-5 inline-block rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700">
          Browse Daikin ACs
        </Link>
      </main>
    );
  }

  const errors = {
    mobile: /^[6-9]\d{9}$/.test(form.mobile) ? null : 'Enter a 10-digit mobile number',
    pincode: !isValidPincode(form.pincode)
      ? 'Enter a 6-digit pincode'
      : !pin?.ok ? (pin?.message || 'We do not deliver to this pincode yet') : null,
    house: form.house.trim() ? null : 'Needed so the driver can find you',
    area: form.area.trim() ? null : 'Add the area, street or sector',
    fullName: form.fullName.trim().length < 2 ? 'Enter your name' : null,
    email: !form.email.trim()
      ? 'Needed for your order confirmation and GST invoice'
      : !/^\S+@\S+\.\S+$/.test(form.email.trim()) ? 'Check this email address' : null,
    gstin: form.gstOn && !GSTIN_RE.test(form.gstin.trim().toUpperCase()) ? 'Enter a valid 15-character GSTIN' : null,
    company: form.gstOn && form.company.trim().length < 2 ? 'Enter the business name on the GSTIN' : null,
  };
  const ok = !Object.values(errors).some(Boolean);
  const err = (k) => (touched ? errors[k] : null);
  const tooMany = quote.units > MAX_UNITS;
  const working = status.kind === 'working';
  const retry = status.kind === 'cancelled' || status.kind === 'failed' || status.kind === 'error';
  const payLabel = working
    ? 'Opening payment…'
    : `${retry ? 'Try again · ' : ''}${plan === 'booking' ? `Book now · Pay ${formatINR(bookNow)}` : `Pay ${formatINR(orderTotal)}`}`;

  const notYou = () => {
    forgetCustomer();
    setForm((f) => ({ ...initialForm(null), pincode: f.pincode }));
    setUseSaved(false);
  };

  const pay = () => {
    setTouched(true);
    if (tooMany || working) return;
    if (!ok) {
      if (useSaved && (errors.house || errors.area || errors.pincode)) setUseSaved(false);
      window.setTimeout(() => {
        document.querySelector('[data-bad="1"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 60);
      return;
    }

    const r = ref || newQuoteRef();
    if (!ref) setRef(r);

    // Installation is one answer for the whole order.
    const payLines = lines.map((l) => ({ ...l, withInstall: install }));
    lines.forEach((l) => {
      if ((l.withInstall === true) !== install) updateLine(l.lineId, { withInstall: install });
    });
    const payQuote = priceQuote(payLines, { delivery });
    const leadForm = toLeadForm(form, install, payQuote);
    const customer = { name: form.fullName.trim(), phone: form.mobile, email: form.email.trim() };
    const gstin = form.gstOn ? form.gstin.trim().toUpperCase() : '';

    saveCustomer({
      mobile: form.mobile,
      fullName: customer.name,
      email: customer.email,
      gstin,
      company: form.gstOn ? form.company.trim() : '',
      address: {
        pincode: form.pincode, house: form.house.trim(), floor: form.floor, lift: form.lift,
        area: form.area.trim(), landmark: form.landmark.trim(), label: form.label,
      },
    });
    savePincode(form.pincode);

    if (!leadSaved.current.has(r)) {
      leadSaved.current.add(r);
      saveLead({ ref: r, kind: 'quote', quote: payQuote, form: leadForm, pin });
      trackQuoteGenerated({ ref: r, total: payQuote.total, units: payQuote.units });
    }
    setAdsUserData(form, pin);
    setStatus({ kind: 'working', message: '' });

    const where = addressLines(form, pin);
    startPayment({
      lines: payLines,
      pincode: form.pincode,
      quoteRef: r,
      expectedTotal: payQuote.total,
      customer,
      wantsInstall: install,
      plan,
      offer: offer ? offer.code : '',
      address: { house: form.house.trim(), area: form.area.trim() },
      details: {
        email: customer.email,
        address: where.join(', '),
        access: floorText(form.floor, form.lift),
        addressType: form.label,
        gstin: gstin ? `${gstin} (${form.company.trim()})` : '',
        preferredDay: form.prefDay || 'no preference',
        exchange: exchangeSummary(payQuote),
      },
      onPaid: (result) => {
        const order = {
          ref: r,
          paidAt: new Date().toISOString(),
          paymentId: result?.payment_id || '',
          total: result?.total ?? payQuote.total,
          paidNow: result?.paidNow ?? payQuote.total,
          balance: result?.balance || 0,
          plan: result?.plan || 'full',
          offer: result?.offer ? { code: result.offer, amount: offer ? Number(offer.amount) : 0 } : null,
          holdDays: BOOKING_HOLD_DAYS,
          units: payQuote.units,
          equipment: payQuote.equipment,
          deliveryAmount: payQuote.deliveryAmount,
          deliveryFree: Boolean(delivery?.free),
          exchangeTotal: payQuote.exchangeTotal,
          exchangeSummary: exchangeSummary(payQuote),
          savings: payQuote.savings,
          name: customer.name,
          mobile: customer.phone,
          email: customer.email,
          address: where,
          deliveryBy: deliveryLabel(),
          prefDay: form.prefDay,
          install,
          gstin,
          lines: payQuote.lines.map((l) => ({
            title: l.model.title,
            idu: l.model.variants[l.kit]?.idu || '',
            kit: l.kit,
            qty: l.qty,
            equipment: l.equipment,
          })),
        };
        try {
          localStorage.setItem(LAST_ORDER_KEY, JSON.stringify(order));
          if (result?.offer) localStorage.removeItem(OFFER_KEY);
        } catch { /* the page still shows it */ }
        trackPurchase({ ref: r, quote: payQuote, paymentId: result?.payment_id, customer, pincode: form.pincode });
        navigate(`/order-confirmed?ref=${encodeURIComponent(r)}`, { replace: true, state: { order } });
        clearCart();
      },
      onFailed: (message) => setStatus({ kind: 'failed', message }),
      onCancelled: () => setStatus((s) => ({ kind: 'cancelled', message: s.kind === 'failed' ? s.message : '' })),
      onError: (message, data) => {
        if (data?.dropOffer) {
          try { localStorage.removeItem(OFFER_KEY); } catch { /* storage off */ }
          setOfferRaw(null);
        }
        setStatus({ kind: 'error', message });
      },
    });
  };

  const deliveryOk = pin?.ok && (
    <p className="mt-2 flex items-start gap-1.5 text-[13px] font-semibold text-green-700">
      <Check className="mt-0.5 h-4 w-4" />
      <span>
        {pin.city}, {pin.state} · Delivery by {deliveryLabel()}
        <span className="block font-normal text-gray-600">
          {delivery?.free ? 'Free delivery' : `Delivery charge ${formatINR(delivery?.price)}`}
        </span>
      </span>
    </p>
  );

  const banner = retry && (
    <div className="flex gap-3 rounded-2xl border-[1.5px] border-amber-500 bg-amber-50 p-4" role="alert">
      <svg viewBox="0 0 24 24" className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.5M12 16.5v.5" /></svg>
      <div className="text-amber-900">
        <p className="font-bold">{status.kind === 'error' ? 'Something went wrong' : 'Payment not completed'}</p>
        <p className="mt-1 text-sm leading-relaxed">
          {status.kind === 'error'
            ? status.message
            : <>No money has been taken.{status.message ? ` ${status.message}` : ''} Your details are saved, so just tap Pay again, or choose a different UPI app or card.</>}
        </p>
        <p className="mt-2 text-sm">
          Need a hand?{' '}
          <a href={`https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(`Hi, I need help paying for my order ${ref}`)}`}
            target="_blank" rel="noopener noreferrer" className="font-semibold text-green-700 underline">WhatsApp us</a>
          {' '}or call <a href={`tel:${BUSINESS.phone}`} className="font-semibold underline">77039 00307</a>.
        </p>
      </div>
    </div>
  );

  const payButton = (extra = '') => (
    <button type="button" onClick={pay} disabled={working || tooMany}
      className={`flex h-[54px] w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-[17px] font-bold text-white transition hover:bg-blue-700 disabled:cursor-wait disabled:opacity-70 ${extra}`}>
      <Lock className="h-[18px] w-[18px]" />
      {payLabel}
    </button>
  );

  const payNote = (
    <p className="mt-1.5 text-center text-[12px] text-gray-600">
      {plan === 'booking' && <span className="block font-semibold text-gray-800">Balance {formatINR(balance)} paid before dispatch</span>}
      UPI · Cards · Net banking · EMI · secured by Razorpay
      {FEE_ACTIVE && <span className="block">Payment charges, if any, are shown in the payment window before you pay.</span>}
    </p>
  );

  // Approved by Saheb (19 Oct 2026) for buyers who fear the site is fake:
  // Razorpay is RBI-authorised, the payee name to check, and the two things
  // we never do. Payee name confirmed as Citi Cool Corporation.
  const paySafely = (
    <Card title="How you pay, safely">
      <ol className="space-y-2.5">
        {[
          <>Payment goes through <b className="text-gray-900">Razorpay</b>, a payment gateway authorised by the RBI.</>,
          <>Check the payee name on your UPI app or card screen. It must say <b className="text-gray-900">Citi Cool Corporation</b>.</>,
          <>We never ask you to pay to a personal UPI ID or bank account.</>,
        ].map((line, i) => (
          <li key={i} className="flex gap-2.5 text-[13.5px] leading-snug text-gray-700">
            <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">{i + 1}</span>
            <span>{line}</span>
          </li>
        ))}
      </ol>
      <p className="mt-3 border-t border-gray-100 pt-2.5 text-[12.5px] text-gray-600">
        Emails about your order only ever come from admin@citicool.in.
      </p>
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>Checkout | Citi Cool</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <main className="mx-auto w-full max-w-5xl pb-44 pt-16 lg:px-6 lg:pb-16 lg:pt-28">
        {/* Phone: the order summary folds away at the top. */}
        <div className="sticky top-16 z-30 border-b border-blue-100 bg-blue-50 lg:hidden">
          <button type="button" onClick={() => setShowItems((v) => !v)} aria-expanded={showItems}
            className="flex h-[52px] w-full items-center justify-between px-4">
            <span className="flex items-center gap-1.5 text-[14px] font-semibold text-blue-800">
              Order summary · {quote.units} item{quote.units === 1 ? '' : 's'}
              <svg viewBox="0 0 20 20" className={`h-4 w-4 transition-transform ${showItems ? 'rotate-180' : ''}`} fill="none"
                stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </span>
            <span className="text-base font-extrabold tabular-nums text-gray-900">{formatINR(orderTotal)}</span>
          </button>
          {showItems && (
            <div className="border-t border-blue-100 bg-white px-4 pb-3">
              <ItemList quote={quote} />
              <button type="button" onClick={openCart} className="mt-1 text-sm font-semibold text-blue-700">Edit cart</button>
            </div>
          )}
        </div>

        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start lg:gap-6">
          <div className="space-y-3 px-4 pt-4 lg:px-0 lg:pt-0">
            <div className="hidden items-center justify-between lg:flex">
              <h1 className="text-2xl font-bold text-gray-900">Checkout</h1>
              <span className="flex items-center gap-1.5 text-sm font-semibold text-green-700"><Lock /> Secure checkout</span>
            </div>

            {banner}

            {navState?.offerProblem && !offer && (
              <p className="rounded-2xl bg-amber-50 p-4 text-sm text-amber-900">{navState.offerProblem} Your order is saved below at the regular price.</p>
            )}
            {welcomeBlocked && (
              <p className="rounded-2xl bg-amber-50 p-4 text-sm text-amber-900">Your ₹300 welcome offer is for new-stock ACs. Add one to use it.</p>
            )}
            {offer && (
              <p className="rounded-2xl bg-green-50 p-4 text-sm font-semibold text-green-800">
                Offer {offer.code} applied: {formatINR(offer.amount)} off your order.
              </p>
            )}

            {useSaved && saved ? (
              <>
                <Card>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-base font-bold text-gray-900">Welcome back, {(form.fullName || '').split(' ')[0] || 'there'}</p>
                      <p className="mt-0.5 text-[13px] text-gray-600">+91 {form.mobile}</p>
                    </div>
                    <button type="button" onClick={notYou} className="shrink-0 text-sm font-semibold text-blue-700">Not you?</button>
                  </div>
                </Card>
                <Card title="Deliver to">
                  <div className="flex gap-3 rounded-xl border-2 border-blue-600 bg-blue-50 p-3">
                    <span className="mt-1 block h-5 w-5 shrink-0 rounded-full border-[6px] border-blue-600 bg-white" aria-hidden="true" />
                    <div className="min-w-0 flex-1 text-sm leading-relaxed text-gray-900">
                      <p className="font-bold">{form.label} · {form.fullName}</p>
                      {addressLines(form, pin).map((line) => <p key={line}>{line}</p>)}
                    </div>
                    <button type="button" onClick={() => setUseSaved(false)} className="shrink-0 self-start text-sm font-semibold text-blue-700">Edit</button>
                  </div>
                  {deliveryOk}
                  {err('pincode') && <p className="mt-2 text-[12px] text-red-600">{err('pincode')}</p>}
                  <button type="button"
                    onClick={() => { setForm((f) => ({ ...f, house: '', floor: '', lift: '', area: '', landmark: '' })); setUseSaved(false); }}
                    className="mt-3 min-h-[46px] w-full rounded-xl border-[1.5px] border-dashed border-gray-300 text-sm font-semibold text-blue-700">
                    + Deliver to a new address
                  </button>
                </Card>
                <Card title="Invoice">
                  <p className="text-sm text-gray-800">Confirmation and GST invoice go to <span className="font-semibold">{form.email}</span></p>
                  {form.gstOn && form.gstin && <p className="mt-1 text-sm text-gray-800">GSTIN {form.gstin.toUpperCase()} · {form.company}</p>}
                  <button type="button" onClick={() => setUseSaved(false)} className="mt-2 text-sm font-semibold text-blue-700">Change</button>
                </Card>
              </>
            ) : (
              <>
                <Card title="Mobile number">
                  <Field error={err('mobile')} hint="For delivery updates on call and WhatsApp.">
                    <span className={`mt-1.5 flex h-[50px] items-center overflow-hidden rounded-xl border bg-white ${err('mobile') ? 'border-red-400 bg-red-50/40' : 'border-gray-300 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500'}`}>
                      <span className="flex h-6 items-center border-r border-gray-200 px-3 text-base font-semibold text-gray-900">+91</span>
                      <input type="tel" inputMode="numeric" autoComplete="tel-national" maxLength={10}
                        value={form.mobile} onChange={digits('mobile', 10)} placeholder="10-digit mobile number"
                        aria-label="Mobile number"
                        className="h-full min-w-0 flex-1 bg-transparent px-3 text-base text-gray-900 outline-none" />
                    </span>
                  </Field>
                </Card>

                <Card title="Delivery address">
                  <div className="space-y-4">
                    <div>
                      <Field label="Pincode" error={err('pincode')}>
                        <input inputMode="numeric" autoComplete="postal-code" maxLength={6}
                          value={form.pincode} onChange={digits('pincode', 6)} placeholder="6 digits"
                          className={`${inputCls(Boolean(err('pincode')))} max-w-[170px]`} />
                      </Field>
                      {deliveryOk}
                      {pin && !pin.ok && (
                        <a href={`https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(`Hi, do you deliver to pincode ${form.pincode}?`)}`} target="_blank" rel="noopener noreferrer"
                          className="mt-2 inline-block text-sm font-semibold text-green-700 underline">
                          WhatsApp us about delivery to {form.pincode}
                        </a>
                      )}
                    </div>

                    <Field label="Flat, house no., building" error={err('house')}>
                      <input value={form.house} onChange={set('house')} autoComplete="address-line1"
                        placeholder="e.g. B-42, Kailash Apartments" className={inputCls(Boolean(err('house')))} />
                    </Field>

                    <div className="flex items-end gap-3">
                      <Field label="Floor" optional className="w-28 shrink-0">
                        <input inputMode="numeric" maxLength={2} value={form.floor} onChange={digits('floor', 2)}
                          placeholder="0 = ground" className={inputCls(false)} />
                      </Field>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-gray-700">Lift access</p>
                        <div className="mt-1.5 flex gap-2">
                          <Chip on={form.lift === 'lift'} onClick={() => pick('lift', form.lift === 'lift' ? '' : 'lift')}>Lift</Chip>
                          <Chip on={form.lift === 'nolift'} onClick={() => pick('lift', form.lift === 'nolift' ? '' : 'nolift')}>No lift</Chip>
                        </div>
                      </div>
                    </div>

                    <Field label="Area, street, sector" error={err('area')}>
                      <input value={form.area} onChange={set('area')} autoComplete="address-line2"
                        placeholder="e.g. South Extension Part 2" className={inputCls(Boolean(err('area')))} />
                    </Field>

                    <Field label="Landmark" optional>
                      <input value={form.landmark} onChange={set('landmark')}
                        placeholder="e.g. opposite the metro station" className={inputCls(false)} />
                    </Field>

                    <Field label="Full name" error={err('fullName')} hint="As it should appear on the invoice.">
                      <input value={form.fullName} onChange={set('fullName')} autoComplete="name"
                        className={inputCls(Boolean(err('fullName')))} />
                    </Field>

                    <div>
                      <p className="text-[13px] font-semibold text-gray-700">Save address as</p>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {['Home', 'Office', 'Other'].map((v) => (
                          <Chip key={v} on={form.label === v} onClick={() => pick('label', v)}>{v}</Chip>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>

                <Card title="Invoice">
                  <Field label="Email" error={err('email')} hint="Order confirmation and GST invoice are sent here.">
                    <input type="email" inputMode="email" autoComplete="email" value={form.email} onChange={set('email')}
                      placeholder="you@example.com" className={inputCls(Boolean(err('email')))} />
                  </Field>
                  {!form.gstOn ? (
                    <button type="button" onClick={() => pick('gstOn', true)}
                      className="mt-3 py-1.5 text-sm font-semibold text-blue-700">
                      + Add GSTIN for a business invoice
                    </button>
                  ) : (
                    <div className="mt-4 space-y-4 rounded-xl bg-gray-50 p-3">
                      <Field label="GSTIN" error={err('gstin')}>
                        <input value={form.gstin} maxLength={15} autoCapitalize="characters"
                          onChange={(e) => pick('gstin', e.target.value.replace(/[^0-9a-z]/gi, '').toUpperCase().slice(0, 15))}
                          placeholder="15 characters" className={`${inputCls(Boolean(err('gstin')))} uppercase`} />
                      </Field>
                      <Field label="Business name" error={err('company')}>
                        <input value={form.company} onChange={set('company')} autoComplete="organization"
                          className={inputCls(Boolean(err('company')))} />
                      </Field>
                      <button type="button" onClick={() => setForm((f) => ({ ...f, gstOn: false, gstin: '', company: '' }))}
                        className="text-sm font-medium text-gray-600 underline underline-offset-2">
                        Remove GSTIN
                      </button>
                    </div>
                  )}
                </Card>
              </>
            )}

            <Card title="Installation">
              <button type="button" role="switch" aria-checked={install}
                onClick={() => { setInstall((v) => !v); track(FUNNEL.INSTALL_TOGGLED, { on: !install, source: 'checkout' }); }}
                className="flex w-full items-center gap-3 text-left">
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-semibold text-gray-900">I need installation</span>
                  <span className="mt-0.5 block text-[13px] leading-snug text-gray-600">
                    Our engineer surveys the site and quotes the fitting separately. It is not part of today&apos;s payment.
                  </span>
                </span>
                <span className={`relative block h-7 w-12 shrink-0 rounded-full transition ${install ? 'bg-blue-600' : 'bg-gray-300'}`} aria-hidden="true">
                  <span className={`absolute top-[3px] block h-[22px] w-[22px] rounded-full bg-white shadow transition-all ${install ? 'left-[23px]' : 'left-[3px]'}`} />
                </span>
              </button>
            </Card>

            <Card title="Preferred delivery day" aside="optional">
              <div className="flex flex-wrap gap-2">
                {days.map((d) => {
                  const v = shortDate(d);
                  return <Chip key={v} on={form.prefDay === v} onClick={() => pick('prefDay', form.prefDay === v ? '' : v)}>{v}</Chip>;
                })}
                <Chip on={form.prefDay === ''} onClick={() => pick('prefDay', '')}>Not sure yet</Chip>
              </div>
              <p className="mt-2 text-[12px] text-gray-600">We call you to confirm the time. {DELIVERY.footnote}</p>
            </Card>

            <Card title="How would you like to pay?">
              <div className="space-y-2.5" role="radiogroup" aria-label="How would you like to pay?">
                <PlanOption on={plan === 'full'} onClick={() => setPlan('full')}
                  title="Pay in full now" amount={formatINR(orderTotal)}>
                  Delivered within 24 hours.
                </PlanOption>
                <PlanOption on={plan === 'booking'} onClick={() => setPlan('booking')}
                  title="Book now" amount={`${formatINR(bookNow)} today`}>
                  Your unit is reserved for you for {BOOKING_HOLD_DAYS} days. We WhatsApp you a photo of your
                  actual unit, then you pay the balance of {formatINR(balance)} before dispatch.
                  <span className="mt-1 block text-[12px] text-gray-500">
                    {deliveryPrice > 0 ? 'Today you pay the delivery charge. ' : ''}If the balance isn&apos;t paid within {BOOKING_HOLD_DAYS} days, the booking amount is refunded less 1% transaction charges.
                  </span>
                </PlanOption>
              </div>
            </Card>

            <div className="lg:hidden">
              <Card title="Price details"><PriceLines quote={quote} delivery={delivery} offer={offer} total={orderTotal} /></Card>
            </div>

            <div className="lg:hidden">{paySafely}</div>

            {tooMany && (
              <p className="rounded-2xl bg-blue-50 p-4 text-sm text-blue-900">
                More than {MAX_UNITS} units is priced as a project. Call {BUSINESS.phone} and we&apos;ll quote it properly.
              </p>
            )}

            <p className="px-2 pb-2 text-center text-[12px] font-medium text-gray-600 lg:hidden">
              GST invoice · Authorised Daikin dealer since 2003 · Cooling Delhi since 1990
            </p>
          </div>

          {/* Desktop: summary and the Pay button stay in view. */}
          <aside className="hidden lg:sticky lg:top-28 lg:block">
            <div className="space-y-3">
              <Card title={`Order summary (${quote.units})`}>
                <ItemList quote={quote} />
                <button type="button" onClick={openCart} className="mb-3 text-sm font-semibold text-blue-700">Edit cart</button>
                <PriceLines quote={quote} delivery={delivery} offer={offer} total={orderTotal} />
                <div className="mt-4">{payButton()}{payNote}</div>
              </Card>
              {paySafely}
              <p className="px-2 text-center text-[12px] font-medium text-gray-600">
                GST invoice · Authorised Daikin dealer since 2003 · Cooling Delhi since 1990
              </p>
            </div>
          </aside>
        </div>
      </main>

      {/* Phone: Pay stays pinned to the bottom. Right padding clears the
          floating call and WhatsApp buttons. */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white px-4 pb-3 pt-2.5 pr-20 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] lg:hidden">
        {retry && <p className="mb-1.5 text-[12px] font-semibold text-amber-800">Payment not completed. Tap to try again.</p>}
        {payButton()}
        {payNote}
      </div>
    </>
  );
}
